import {
  Store as VuexStore,
  ActionContext,
  ActionTree,
  CommitOptions,
  DispatchOptions,
  GetterTree,
  Module,
  MutationTree,
} from "vuex";
import { MainMenuItem } from "@/models/main-menu-item";
import { FolderItem, MainMenuState } from "@/store/modules/mainMenu/main-menu-state";
import { RootState } from "@/store/root-state";
import menuItems from "@/menuItems";

const MENU_ITEMS_STORAGE_KEY = "MENU_ITEMS_STORAGE_KEY_V3";
const FOLDER_ITEMS_STORAGE_KEY = "FOLDER_ITEMS_STORAGE_KEY_";
// initial state
const state: MainMenuState = {
  folderItems: {},
  menuItems: [],
};

// getters
export type Getters = {
  getFolderItems: (state: MainMenuState) => (folderId?: string) => string[][];
  getMenuItems: (state: MainMenuState) => () => MainMenuItem[];
};

const getters: GetterTree<MainMenuState, RootState> & Getters = {
  getFolderItems:
    () =>
    (folderId?: string): string[][] => {
      const menuAsJson = localStorage.getItem(FOLDER_ITEMS_STORAGE_KEY + (folderId || ""));
      const items = (menuAsJson && (JSON.parse(menuAsJson) as string[][])) || [];
      return items;
    },
  getMenuItems: () => () => {
    const storedAsJson = localStorage.getItem(MENU_ITEMS_STORAGE_KEY);
    const stored = (storedAsJson && (JSON.parse(storedAsJson) as string[][])) || [];

    const result = [] as MainMenuItem[];
    const localMenuItems = Object.assign([], menuItems);
    for (let i = 0; i < stored.length; i++) {
      stored.forEach((items) => {
        const index = localMenuItems.findIndex((x: any) => x.id == items[i]);
        index >= 0 && result.push(localMenuItems.splice(index, 1)[0]);
      });
    }
    result.push(...localMenuItems);
    return result;
  },
};

// actions
export interface Actions {
  changeMenuItems({ commit }: ActionContext<MainMenuState, RootState>): void;
}

const actions: ActionTree<MainMenuState, RootState> = {
  changeMenuItems({ commit }: ActionContext<MainMenuState, RootState>, items: MainMenuItem[]) {
    commit("setMenuItems", items);
  },
};

// mutations
export type Mutations = {
  setMenuItems(state: MainMenuState, payload: string[][]): void;
  setFolderItems(state: MainMenuState, payload: FolderItem): void;
};

const mutations: MutationTree<MainMenuState> & Mutations = {
  setMenuItems(state: MainMenuState, payload: string[][]): void {
    localStorage.setItem(MENU_ITEMS_STORAGE_KEY, JSON.stringify(payload));
  },
  setFolderItems(state: MainMenuState, payload: FolderItem): void {
    localStorage.setItem(FOLDER_ITEMS_STORAGE_KEY + (payload.id || ""), JSON.stringify(payload.items));
  },
};

// store
export type MainMenuStore<S = MainMenuState> = Omit<VuexStore<S>, "getters" | "commit" | "dispatch"> & {
  commit<K extends keyof Mutations, P extends Parameters<Mutations[K]>[1]>(
    key: K,
    payload: P,
    options?: CommitOptions
  ): ReturnType<Mutations[K]>;
} & {
  // TODO: How to configure a module without any actions?
  dispatch<K extends keyof Actions>(
    key: K,
    payload: Parameters<Actions[K]>[1],
    options?: DispatchOptions
  ): ReturnType<Actions[K]>;
} & {
  getters: {
    [K in keyof Getters]: ReturnType<Getters[K]>;
  };
};

const namespaced = false;

export const mainMenuItems: Module<MainMenuState, RootState> = {
  namespaced,
  state,
  getters,
  actions,
  mutations,
};
